import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Paper,
  Popper,
  Typography,
  TextField,
  Fade,
  Theme,
  ClickAwayListener
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useRef,
  useState
} from "react";
import SectionWrapper from "../SectionWrapper/SectionWrapper";
import clsx from "clsx";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useTranslation } from "react-i18next";
import {mainPageIDs} from "../../qaIDs.json"

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    padding: theme.spacing(3),
    height: 120,
    [theme.breakpoints.down("md")]: {
      display: "block",
      height: "auto"
    }
  },
  input: {
    height: theme.spacing(5.25)
  },
  inputWrapper: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      marginRight: 0
    }
  },
  trackingNumber: {
    [theme.breakpoints.down("md")]: {
      width: "100%",
      marginBottom: theme.spacing(2)
    }
  },
  postalCode: {
    [theme.breakpoints.down("md")]: {
      width: "calc(100% - 107px)",
      display: "inline-block"
    }
  },
  label: {
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "15px",
    display: "flex",
    marginBottom: theme.spacing(0.75)
  },
  btn: {
    height: theme.spacing(5.25),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2.625),
    [theme.breakpoints.down("md")]: {
      marginLeft: theme.spacing(2),
      marginTop: theme.spacing(2.5),
      width: "90px",
      verticalAlign: "bottom"
    }
  },
  helperText: {
    position: "absolute",
    bottom: -19,
    lineHeight: "16px",
    fontSize: "12px"
  },
  popper: {
    padding: theme.spacing(0, 3)
  },
  paper: {
    backgroundColor: "#5F6368",
    color: "#FFF",
    "& .MuiTypography-root": {
      fontSize: "15px",
      padding: theme.spacing(1.5, 2, 1.5, 1.5),
      lineHeight: "24px",
      fontWeight: "normal",
      display: "flex",
      "& .MuiSvgIcon-root": {
        marginRight: theme.spacing(1)
      }
    }
  },
  arrow: {
    width: 0,
    height: 0,
    position: "absolute",
    left: 0,
    right: 0,
    transform: "translate(0) !important",
    margin: "auto",
    top: 9,
    borderStyle: "solid",
    borderWidth: "0 8px 10px 8px",
    borderColor: "transparent transparent #5f6368 transparent"
  }
}));

export default function Form({
  tracking_number,
  postal_code,
  setInputValuesGlobal,
  postalAvailable,
  tooltipOpen,
  setTooltipOpen
}: {
  tracking_number: string | undefined;
  postal_code: string | undefined;
  setInputValuesGlobal: React.Dispatch<React.SetStateAction<any>>;
  postalAvailable: boolean | undefined;
  tooltipOpen: boolean;
  setTooltipOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const classes = useStyles();

  const handleSubmit = () => {
    setInputValuesGlobal({ ...inputValues });
  };

  const popperAnchorEl = useRef();

  const [inputValues, setInputValues] = useState<any>({
    trackingNumber: tracking_number || "",
    postalCode: postal_code || ""
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValues((prevValues: any) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
  };

  const { t } = useTranslation();

  const paperRef = useRef<HTMLDivElement | null>(null);

  return (
    <SectionWrapper>
      <ClickAwayListener
        onClickAway={() => {
          // console.log("HERE", tooltipOpen);
          if (tooltipOpen) setTooltipOpen(false);
        }}
      >
        <Popper
          open={tooltipOpen}
          anchorEl={popperAnchorEl.current}
          placement={"bottom"}
          modifiers={[
            {
              name: "arrow",
              enabled: true,
              options: {
                element: paperRef.current
              }
            }
          ]}
          transition
          disablePortal
          className={classes.popper}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Box sx={{ paddingTop: 2.25 }}>
                <span className={classes.arrow} ref={paperRef}></span>
                <Paper className={classes.paper}>
                  <Typography sx={{ p: 2 }}>
                    <InfoOutlinedIcon />
                    <span>{t("Add your postal code to see more details")}</span>
                  </Typography>
                </Paper>
              </Box>
            </Fade>
          )}
        </Popper>
      </ClickAwayListener>
      <Box className={classes.root}>
        <div className={clsx(classes.inputWrapper, classes.trackingNumber)}>
          <InputLabel className={classes.label} htmlFor={mainPageIDs.trackingNumber}>
            {t("Tracking number")}
          </InputLabel>
          <FormControl
            sx={{
              width: "100%"
            }}
          >
            <OutlinedInput
              id={mainPageIDs.trackingNumber}
              className={classes.input}
              value={inputValues.trackingNumber}
              onChange={handleChange}
              name="trackingNumber"
            />
            <FormHelperText className={classes.helperText}></FormHelperText>
          </FormControl>
        </div>
        <div className={clsx(classes.inputWrapper, classes.postalCode)}>
          <InputLabel className={classes.label} htmlFor={mainPageIDs.postalCode}>
            {t("Postal code")}
          </InputLabel>
          <FormControl
            sx={{
              width: "100%"
            }}
          >
            <OutlinedInput
              id={mainPageIDs.postalCode}
              className={classes.input}
              value={inputValues.postalCode}
              onChange={handleChange}
              name="postalCode"
              ref={popperAnchorEl}
            />
            <FormHelperText className={classes.helperText}></FormHelperText>
          </FormControl>
        </div>
        <Button
          disableElevation
          onClick={handleSubmit}
          className={classes.btn}
          variant="contained"
        >
          {t("Search")}
        </Button>
      </Box>
    </SectionWrapper>
  );
}
